/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useState, useEffect } from 'react';
import useInviteUser from '../../redux/features/users/invite/useUsersInvite';
import useGetUsersBy from '../../redux/features/users/get/useGetUsersBy';

export function useInviteUserDialog(orgId) {
  const [fieldToSearch, setFieldToSearch] = useState();
  const [isValid, setIsValid] = useState(false);
  const [autofillUsers, setAutofillUsers] = useState([]);

  const {
    isLoading,
    invitations,
    successDialog,
    dialog,
    onSubmit,
    onUpdateInvitations,
    onToggleDialog,
    onToggleSuccessDialog,
  } = useInviteUser(orgId);

  const { users } = useGetUsersBy(fieldToSearch, { organizationIdMembersToBeSkipped: orgId });

  useEffect(() => {
    if (
      invitations?.roles?.length === 0 ||
      (invitations?.userIds?.length === 0 && invitations?.emailAddresses?.length === 0)
    ) {
      setIsValid(false);
      return;
    }

    for (const email of invitations.emailAddresses) {
      if (!validateSelection(email)) {
        setIsValid(false);
        return;
      }
    }

    setIsValid(true);
  }, [invitations]);

  useEffect(() => {
    if (!users) return;

    if (users.length === 0) {
      setAutofillUsers([]);
      return;
    }

    const autofillOptions = users.map(user => ({
      id: user.id,
      label: `${user.firstName} ${user.lastName} ${user.username}`,
      value: user.username,
    }));

    setAutofillUsers(autofillOptions);
  }, [users]);

  const onRoleChange = (role) => {
    const roles = [...invitations.roles];

    const indexOfRole = roles.findIndex(value => value === role);
    if (indexOfRole > -1) {
      roles.splice(indexOfRole, 1);
    } else {
      roles.push(role);
    }

    const newInvitations = {
      userIds: [...invitations.userIds],
      roles,
      emailAddresses: [...invitations.emailAddresses],
    };
    onUpdateInvitations(newInvitations);
  };

  const onUserChange = (selectedUsers = []) => {
    const selectedUserIds = [];
    const emailAddresses = [];

    for (const user of selectedUsers) {
      if (user.id) {
        selectedUserIds.push(user.id);
      } else {
        emailAddresses.push(user);
      }
    }

    const newInvitations = { roles: [...invitations.roles], userIds: selectedUserIds, emailAddresses };
    onUpdateInvitations(newInvitations);
  };

  const onKeyUp = (value) => {
    setFieldToSearch(value);
  };

  const validateSelection = (value) => {
    if (!value) return true;

    return typeof value === 'object' || /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value);
  };

  return {
    isLoading,
    successDialog,
    invitations,
    dialog,
    isValid,
    autofillUsers,
    orgId,
    fieldToSearch,
    validateSelection,
    onSubmit,
    onToggleDialog,
    onToggleSuccessDialog,
    onRoleChange,
    onUserChange,
    onKeyUp,
  };
}
